import { useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  ArrowLeft,
  KeyRound,
  Mail,
  Calendar,
  Building2,
  ShieldOff,
  UserCheck,
  UserX,
  RotateCcw,
  CheckCircle,
  Briefcase,
  Activity,
} from 'lucide-react';
import { emailSchema } from '@/lib/validationSchemas';
import {
  useUser,
  useUserOrganizations,
  useUserWorkspaces,
  useUserActivity,
  useSendPasswordReset,
  useUpdateUserStatus,
  useResetTwoFactor,
  useTwoFactorStatus,
  useRestoreUser,
  useVerifyUserEmail,
  useUpdateUserEmail,
} from '@/hooks/api/useUsers';
import { ActivityFilters } from '@/api/users';
import { Button } from '@/components/ui/button';
import { CopyableId } from '@/components/CopyableId';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { ErrorMessage } from '@/components/ui/error-message';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import Routes from '@/router/routes';
import { formatDate, formatDateTime } from '@/lib/date';

const emailUpdateSchema = Yup.object().shape({
  email: emailSchema,
  sendConfirmation: Yup.boolean().default(true),
});

type UpdateEmailForm = Yup.InferType<typeof emailUpdateSchema>;

export default function SuperAdminSingleUser() {
  const { user_id } = useParams<{ user_id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('organizations');
  const [updateEmailDialogOpen, setUpdateEmailDialogOpen] = useState(false);
  const [activityFilters, setActivityFilters] = useState<ActivityFilters>({ dateRange: 'all' });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    variant?: 'default' | 'destructive';
    confirmLabel?: string;
  }>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  // Stable filter references — inline objects cause infinite re-renders
  // because React Query treats each new object reference as a key change.
  // Matches the pattern applied in SuperAdminSingleWorkspace by #1423.
  const defaultFilters = useMemo(() => ({ page: 1, limit: 100 }), []);

  const {
    data: user,
    isLoading: isLoadingUser,
    isError: isUserError,
    refetch: refetchUser,
  } = useUser(user_id!);
  const { data: organizationsData, isLoading: isLoadingOrgs } = useUserOrganizations(
    user_id!,
    defaultFilters,
  );
  const { data: workspacesData, isLoading: isLoadingWorkspaces } = useUserWorkspaces(
    user_id!,
    defaultFilters,
  );
  const { data: activityData, isLoading: isLoadingActivity } = useUserActivity(
    user_id!,
    activityFilters,
  );
  const { data: twoFactorStatus } = useTwoFactorStatus(user_id!);
  const { mutate: sendPasswordReset, isPending: isSendingReset } = useSendPasswordReset();
  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateUserStatus();
  const { mutate: resetTwoFactor, isPending: isResetting2FA } = useResetTwoFactor();
  const { mutate: restoreUser, isPending: isRestoring } = useRestoreUser();
  const { mutate: verifyEmail, isPending: isVerifyingEmail } = useVerifyUserEmail();
  const { mutate: updateEmail, isPending: isUpdatingEmail } = useUpdateUserEmail();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset: resetEmailForm,
    formState: { errors: emailErrors },
  } = useForm<UpdateEmailForm>({
    resolver: yupResolver(emailUpdateSchema),
    defaultValues: { email: '', sendConfirmation: true },
  });

  const handleBack = () => {
    navigate(Routes.super_admin_dashboard_users.path);
  };

  const handleResetPassword = () => {
    if (!user_id || !user) return;
    setConfirmDialog({
      open: true,
      title: 'Reset Password',
      description: `Are you sure you want to send a password reset email to ${user.email}?`,
      confirmLabel: 'Send Reset Email',
      onConfirm: () => {
        sendPasswordReset(user_id, {
          onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
        });
      },
    });
  };

  const handleToggleStatus = () => {
    if (!user_id || !user) return;
    const isActive = user.status === 'active';
    setConfirmDialog({
      open: true,
      title: isActive ? 'Deactivate User' : 'Activate User',
      description: isActive
        ? `Are you sure you want to deactivate ${user.email}? The user will no longer be able to access the platform.`
        : `Are you sure you want to activate ${user.email}?`,
      confirmLabel: isActive ? 'Deactivate' : 'Activate',
      variant: isActive ? 'destructive' : 'default',
      onConfirm: () => {
        const newStatus = isActive ? 'deactivated' : 'active';
        updateStatus(
          { id: user_id, payload: { status: newStatus as 'active' | 'deactivated' } },
          { onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })) },
        );
      },
    });
  };

  const handleReset2FA = () => {
    if (!user_id || !user) return;
    setConfirmDialog({
      open: true,
      title: 'Reset Two-Factor Authentication',
      description: `Are you sure you want to reset 2FA for ${user.email}? The user will need to set up 2FA again on their next login.`,
      confirmLabel: 'Reset 2FA',
      onConfirm: () => {
        resetTwoFactor(user_id, {
          onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
        });
      },
    });
  };

  const handleRestore = () => {
    if (!user_id || !user) return;
    setConfirmDialog({
      open: true,
      title: 'Restore User',
      description: `Are you sure you want to restore ${user.email}? The user will be able to access the platform again.`,
      confirmLabel: 'Restore User',
      onConfirm: () => {
        restoreUser(user_id, {
          onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
        });
      },
    });
  };

  const handleVerifyEmail = () => {
    if (!user_id || !user) return;
    setConfirmDialog({
      open: true,
      title: 'Verify Email',
      description: `Are you sure you want to manually verify the email for ${user.email}? This will mark the email as verified without the user confirming it themselves.`,
      confirmLabel: 'Verify Email',
      onConfirm: () => {
        verifyEmail(user_id, {
          onSuccess: () => setConfirmDialog((prev) => ({ ...prev, open: false })),
        });
      },
    });
  };

  const handleOpenUpdateEmail = () => {
    resetEmailForm({ email: '', sendConfirmation: true });
    setUpdateEmailDialogOpen(true);
  };

  const handleUpdateEmailSubmit = (data: UpdateEmailForm) => {
    if (!user_id) return;
    updateEmail(
      { id: user_id, email: data.email, sendConfirmation: data.sendConfirmation },
      { onSuccess: () => setUpdateEmailDialogOpen(false) },
    );
  };

  const formatActivityAction = (action: string) => {
    return action
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="active">Active</Badge>;
      case 'pending':
        return <Badge variant="pending">Pending</Badge>;
      case 'deactivated':
        return <Badge variant="error">Deactivated</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const getUserDisplayName = (user: any) => {
    const fullName = [user.name, user.last_name || user.lastName].filter(Boolean).join(' ');
    return fullName || user.email;
  };

  const getUserInitials = (user: any) => {
    const displayName = getUserDisplayName(user);
    const parts = displayName.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return displayName.substring(0, 2).toUpperCase();
  };

  if (isLoadingUser) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Skeleton className="h-8 w-64" />
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-6">
          <div className="flex items-start gap-6">
            <Skeleton className="h-20 w-20 rounded-full" />
            <div className="space-y-3 flex-1">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isUserError) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-red-600 mb-4">Failed to load data. Please try again.</p>
        <Button variant="outline" onClick={() => refetchUser()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold text-foreground">User Not Found</h1>
        </div>
        <div className="bg-card rounded-lg shadow-sm border border-border p-8 text-center">
          <p className="text-muted-foreground">The requested user could not be found.</p>
          <Button onClick={handleBack} className="mt-4">
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumbs
        items={[
          { label: 'Users', path: Routes.super_admin_dashboard_users.path },
          { label: getUserDisplayName(user) },
        ]}
      />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{getUserDisplayName(user)}</h1>
              {user.status === 'deactivated' && <Badge variant="deleted">Deactivated</Badge>}
            </div>
            <p className="text-muted-foreground">User details and memberships</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {user.status === 'deactivated' ? (
            <Button onClick={handleRestore} disabled={isRestoring}>
              <RotateCcw className="mr-2 h-4 w-4" />
              Restore User
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={handleOpenUpdateEmail}>
                <Mail className="mr-2 h-4 w-4" />
                Update Email
              </Button>
              <Button variant="outline" onClick={handleResetPassword} disabled={isSendingReset}>
                <KeyRound className="mr-2 h-4 w-4" />
                Reset Password
              </Button>
              <Button
                variant="outline"
                onClick={handleReset2FA}
                disabled={isResetting2FA || !twoFactorStatus?.twoFactorEnabled}
                title={
                  !twoFactorStatus?.twoFactorEnabled
                    ? '2FA is not enabled for this user'
                    : undefined
                }
              >
                <ShieldOff className="mr-2 h-4 w-4" />
                Reset 2FA
              </Button>
              {user.status === 'active' ? (
                <Button
                  variant="outline"
                  onClick={handleToggleStatus}
                  disabled={isUpdatingStatus}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <UserX className="mr-2 h-4 w-4" />
                  Deactivate
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={handleToggleStatus}
                  disabled={isUpdatingStatus}
                  className="text-green-600 hover:text-green-700 hover:bg-green-50"
                >
                  <UserCheck className="mr-2 h-4 w-4" />
                  Activate
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* User Profile Card */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Profile Information</h2>
        <div className="flex items-start gap-6">
          <Avatar className="h-20 w-20">
            <AvatarFallback className="text-lg bg-blue-100 text-blue-700">
              {getUserInitials(user)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Mail className="h-4 w-4" />
                  <span className="font-medium">Email</span>
                </div>
                <p className="text-foreground">{user.email}</p>
                <div className="mt-1 flex items-center gap-2">
                  {user.email_verified ? (
                    <Badge variant="success" className="text-xs">
                      Verified
                    </Badge>
                  ) : (
                    <>
                      <Badge variant="warning" className="text-xs">
                        Not Verified
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={handleVerifyEmail}
                        disabled={isVerifyingEmail}
                      >
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Verify
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <span className="font-medium">Status</span>
                </div>
                <div>{getStatusBadge(user.status)}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
              <div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                  <Calendar className="h-4 w-4" />
                  <span className="font-medium">Joined</span>
                </div>
                <p className="text-foreground">{formatDate(user.createdAt)}</p>
              </div>
              <CopyableId value={user._id} label="User ID" />
            </div>

            <div className="grid grid-cols-3 gap-4 pt-4 border-t">
              <div>
                <p className="text-sm text-muted-foreground">Organizations</p>
                <p className="text-2xl font-bold text-foreground">{user.organizationCount || 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Workspaces</p>
                <p className="text-2xl font-bold text-foreground">{user.workspaceCount || 0}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Projects</p>
                <p className="text-2xl font-bold text-foreground">{user.projectCount || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs: Organizations, Workspaces, Activity */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="organizations">
            <Building2 className="h-4 w-4 mr-2" />
            Organizations
          </TabsTrigger>
          <TabsTrigger value="workspaces">
            <Briefcase className="h-4 w-4 mr-2" />
            Workspaces
          </TabsTrigger>
          <TabsTrigger value="activity">
            <Activity className="h-4 w-4 mr-2" />
            Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="organizations" className="mt-4">
          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Organization Memberships</h2>
            {isLoadingOrgs ? (
              <TableSkeleton columns={3} rows={3} headers={['Organization', 'Role', 'Joined']} />
            ) : !organizationsData || organizationsData.data.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No organization memberships found</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Organization</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {organizationsData.data.map((org) => (
                      <TableRow key={org._id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Link
                              to={`${Routes.super_admin_dashboard_organizations.path}/${org._id}`}
                              className="font-medium text-blue-600 hover:underline"
                            >
                              {org.name}
                            </Link>
                            {org.subscribed && (
                              <Badge variant="paid" className="text-xs">
                                Subscribed
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="default" className="capitalize">
                            {org.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(org.joinedAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="workspaces" className="mt-4">
          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Workspace Memberships</h2>
            {isLoadingWorkspaces ? (
              <TableSkeleton
                columns={4}
                rows={3}
                headers={['Workspace', 'Organization', 'Role', 'Joined']}
              />
            ) : !workspacesData || workspacesData.data.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No workspace memberships found</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Workspace</TableHead>
                      <TableHead>Organization</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workspacesData.data.map((ws) => (
                      <TableRow key={ws._id}>
                        <TableCell>
                          <Link
                            to={`${Routes.super_admin_dashboard_workspaces.path}/${ws._id}`}
                            className="font-medium text-blue-600 hover:underline"
                          >
                            {ws.name}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Link
                            to={`${Routes.super_admin_dashboard_organizations.path}/${ws.organizationId}`}
                            className="text-blue-600 hover:underline"
                          >
                            {ws.organizationName}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Badge variant="default" className="capitalize">
                            {ws.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(ws.joinedAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <div className="bg-card rounded-lg shadow-sm border border-border p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Activity Log</h2>
              <Select
                value={activityFilters.dateRange || 'all'}
                onValueChange={(value) =>
                  setActivityFilters({ dateRange: value as ActivityFilters['dateRange'] })
                }
              >
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Date range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All time</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {isLoadingActivity ? (
              <TableSkeleton
                columns={3}
                rows={5}
                headers={['Event Type', 'Resource', 'Timestamp']}
              />
            ) : !activityData || activityData.data.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">No activity found for this period</p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event Type</TableHead>
                      <TableHead>Resource</TableHead>
                      <TableHead>Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activityData.data.map((event) => (
                      <TableRow key={event._id}>
                        <TableCell className="font-medium">
                          {formatActivityAction(event.action)}
                        </TableCell>
                        <TableCell className="text-muted-foreground capitalize">
                          {event.resource}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDateTime(event.timestamp)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Update Email Dialog */}
      <Dialog
        open={updateEmailDialogOpen}
        onOpenChange={(open) => {
          if (!open) resetEmailForm({ email: '', sendConfirmation: true });
          setUpdateEmailDialogOpen(open);
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Update Email Address</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(handleUpdateEmailSubmit)}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Current Email</Label>
                <Input value={user.email} disabled className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-email">New Email</Label>
                <Input
                  id="new-email"
                  type="email"
                  placeholder="Enter new email address"
                  {...register('email')}
                  hasError={!!emailErrors.email}
                />
                <ErrorMessage message={emailErrors.email?.message} />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send-confirmation"
                  checked={watch('sendConfirmation')}
                  onCheckedChange={(checked) => setValue('sendConfirmation', !!checked)}
                />
                <Label htmlFor="send-confirmation" className="cursor-pointer font-normal">
                  Send confirmation email to new address
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setUpdateEmailDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isUpdatingEmail}>
                {isUpdatingEmail ? 'Updating...' : 'Update Email'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={confirmDialog.confirmLabel}
        onConfirm={confirmDialog.onConfirm}
        variant={confirmDialog.variant}
        loading={
          isSendingReset || isUpdatingStatus || isResetting2FA || isRestoring || isVerifyingEmail
        }
      />
    </div>
  );
}
